import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";
import {
  Mail,
  Search,
  Filter,
  RefreshCw,
  Upload,
  FileText,
  CheckCircle,
  Clock,
  Archive,
  AlertCircle,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Package,
  Ship,
  Truck,
  MessageSquare,
  Eye,
  Send,
  X,
  Loader2,
  User,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { API_URL } from "../config/api";


const statusConfig = {
  pending: { label: "Needs Review", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
  draft: { label: "Ready", color: "bg-blue-100 text-blue-800", icon: FileText },
  processed: { label: "Processed", color: "bg-green-100 text-green-800", icon: CheckCircle },
  converted: { label: "Converted", color: "bg-purple-100 text-purple-800", icon: CheckCircle },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-600", icon: Archive },
};

const inboxConfig = {
  all: { label: "All Inboxes", color: "bg-slate-100 text-slate-800" },
  roket: { label: "roket@europacific.com", color: "bg-blue-100 text-blue-800" },
  update: { label: "update.myeuropacific@", color: "bg-green-100 text-green-800" },
  newsletter: { label: "newsletter@", color: "bg-purple-100 text-purple-800" },
  upload: { label: "Manual Upload", color: "bg-orange-100 text-orange-800" },
};

const sectionIcons = {
  quotation: Ship,
  position_folder: Package,
  transport_order: Truck,
  container_tracking: Ship,
  lcl_tracking: Package,
  port_tracking: Ship,
  general: MessageSquare,
  unassigned: AlertCircle,
};

export default function EmailRepositoryPage() {
  const { token } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionLabels, setSectionLabels] = useState({});
  const [statusCounts, setStatusCounts] = useState({});
  const [sectionCounts, setSectionCounts] = useState({});
  const [triggers, setTriggers] = useState({});
  
  // Email direction toggle (inbound = received, outbound = sent)
  const [emailDirection, setEmailDirection] = useState("inbound");
  
  // Filters
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedInbox, setSelectedInbox] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inboxCounts, setInboxCounts] = useState({});
  
  // Modals
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAssignStaffModal, setShowAssignStaffModal] = useState(false);
  const [reassignSection, setReassignSection] = useState("");
  const [parsing, setParsing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  
  // Staff assignment
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assigningStaff, setAssigningStaff] = useState(false);

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      
      if (emailDirection === "outbound") {
        // Fetch from outbound_email_repository
        const params = new URLSearchParams();
        if (searchQuery) params.append("search", searchQuery);
        params.append("skip", (page - 1) * 20);
        params.append("limit", 20);
        
        const response = await axios.get(
          `${API_URL}/api/outbound-emails?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Transform outbound emails to match inbound format for display
        const outboundEmails = (response.data.emails || []).map(e => ({
          ...e,
          from_email: e.sender_email || "myeuropacific.noreply@europacific.com",
          from_name: "Europacific",
          to_email: e.to_email,  // Keep original field
          customer_email: e.to_email,  // Also map to customer_email for compatibility
          subject: e.subject,
          date: e.sent_at,
          status: e.status,
          direction: "outbound"
        }));
        
        setEmails(outboundEmails);
        setTotalPages(Math.ceil((response.data.total || 0) / 20) || 1);
        setStatusCounts({});
        setSectionCounts({});
        setInboxCounts({});
      } else {
        // Original inbound fetch
        const params = new URLSearchParams();
        if (selectedStatus) params.append("status", selectedStatus);
        if (selectedSection) params.append("section", selectedSection);
        if (selectedInbox) params.append("inbox_account", selectedInbox);
        if (searchQuery) params.append("search", searchQuery);
        params.append("page", page);
        params.append("per_page", 20);
        
        const response = await axios.get(
          `${API_URL}/email/repository?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Mark as inbound for display
        const inboundEmails = (response.data.emails || []).map(e => ({
          ...e,
          direction: "inbound"
        }));
        
        setEmails(inboundEmails);
        setTotalPages(response.data.total_pages);
        setSectionLabels(response.data.section_labels || {});
        setStatusCounts(response.data.status_counts || {});
        setSectionCounts(response.data.section_counts || {});
        setInboxCounts(response.data.inbox_counts || {});
      }
    } catch (error) {
      toast.error("Failed to fetch emails");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, emailDirection, selectedStatus, selectedSection, selectedInbox, searchQuery, page]);

  const fetchTriggers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/email/triggers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTriggers(response.data.triggers);
    } catch (error) {
      console.error("Failed to fetch triggers:", error);
    }
  }, [token]);

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/email/staff-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error("Failed to fetch staff list:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchEmails();
    fetchTriggers();
    fetchStaffList();
  }, [fetchEmails, fetchTriggers, fetchStaffList]);

  const handleUploadEml = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API_URL}/email/upload-eml`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Check for duplicate response
      if (response.data.duplicate) {
        toast.warning(
          `Duplicate email detected! Already exists as ${response.data.existing_ticket}`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Email uploaded! Ticket: ${response.data.ticket_number}`);
      }
      setShowUploadModal(false);
      fetchEmails();
    } catch (error) {
      // Handle duplicate in error response too (different status codes)
      if (error.response?.data?.duplicate) {
        toast.warning(
          `Duplicate email! Already exists as ${error.response.data.existing_ticket}`,
          { duration: 5000 }
        );
        setShowUploadModal(false);
      } else {
        toast.error(error.response?.data?.detail || error.response?.data?.message || "Failed to upload email");
      }
    }
  };

  const handleParseEmail = async (email) => {
    setParsing(true);
    try {
      const response = await axios.post(
        `${API_URL}/email/repository/${email.id}/parse`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Email parsed successfully!");
        setSelectedEmail({ ...email, parsed_data: response.data.parsed_data });
        fetchEmails();
      } else {
        toast.error("Could not parse email content");
      }
    } catch (error) {
      toast.error("Failed to parse email");
    } finally {
      setParsing(false);
    }
  };

  const handleConvertToQuote = async (email) => {
    setConverting(true);
    try {
      const response = await axios.post(
        `${API_URL}/email/repository/${email.id}/convert-to-quote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Quote ${response.data.quote_number} created!`);
      setShowDetailModal(false);
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert to quote");
    } finally {
      setConverting(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignSection) return;
    try {
      const response = await axios.put(
        `${API_URL}/email/repository/${selectedEmail.id}/reassign`,
        { target_section: reassignSection, auto_process: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      if (data.auto_processed && data.process_result) {
        // Show specific success message based on what was processed
        if (data.process_result.event_type) {
          toast.success(`Email reassigned and processed! Created ${data.process_result.event_label || data.process_result.event_type} event for ${data.process_result.pf_number || data.process_result.mrn || 'shipment'}`);
        } else if (data.process_result.quote_id) {
          toast.success(`Email reassigned and quote draft created! Company: ${data.process_result.parsed?.customer_company || 'Unknown'}`);
        } else if (data.process_result.containers_found) {
          toast.success(`Email reassigned! Found ${data.process_result.containers_found.length} container(s) to track`);
        } else {
          toast.success("Email reassigned and processed successfully!");
        }
      } else {
        toast.success("Email reassigned successfully!");
      }
      
      setShowReassignModal(false);
      setShowDetailModal(false);
      fetchEmails();
    } catch (error) {
      toast.error("Failed to reassign email");
    }
  };

  const handleArchive = async (email) => {
    try {
      await axios.put(
        `${API_URL}/email/repository/${email.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email archived");
      setShowDetailModal(false);
      fetchEmails();
    } catch (error) {
      toast.error("Failed to archive email");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(
        `${API_URL}/email/repository/${selectedEmail.id}/add-note`,
        { note: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Note added");
      setNewNote("");
      setSelectedEmail({
        ...selectedEmail,
        notes: [...(selectedEmail.notes || []), { text: newNote, at: new Date().toISOString() }],
      });
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaffId) {
      toast.error("Please select a staff member");
      return;
    }
    setAssigningStaff(true);
    try {
      const staffMember = staffList.find(s => s.id === selectedStaffId);
      const response = await axios.put(
        `${API_URL}/email/repository/${selectedEmail.id}/assign-staff`,
        { 
          assigned_staff_id: selectedStaffId,
          assigned_staff_name: staffMember?.name || staffMember?.email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Staff assigned successfully");
      setShowAssignStaffModal(false);
      setSelectedStaffId("");
      // Update selected email with new assignment
      setSelectedEmail({
        ...selectedEmail,
        assigned_staff_id: response.data.assigned_staff_id,
        assigned_staff_name: response.data.assigned_staff_name
      });
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    } finally {
      setAssigningStaff(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="p-6" data-testid="email-repository-page">
        {/* Header with Direction Toggle */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              {emailDirection === "inbound" ? "Inbound" : "Outbound"} Email Repository
            </h1>
            <p className="text-slate-500">
              {emailDirection === "inbound" 
                ? "Manage incoming customer requests and route them to the right section"
                : "View sent emails from the system (reactivation, offers, notifications)"
              }
            </p>
          </div>
          <div className="flex gap-2 bg-slate-200 p-1 rounded-lg">
            <Button
              variant={emailDirection === "inbound" ? "default" : "ghost"}
              size="sm"
              onClick={() => { setEmailDirection("inbound"); setPage(1); }}
              data-testid="toggle-inbound"
            >
              <Mail className="h-4 w-4 mr-2" />
              Received
            </Button>
            <Button
              variant={emailDirection === "outbound" ? "default" : "ghost"}
              size="sm"
              onClick={() => { setEmailDirection("outbound"); setPage(1); }}
              data-testid="toggle-outbound"
            >
              <Send className="h-4 w-4 mr-2" />
              Sent
            </Button>
          </div>
        </div>

      {/* Stats Cards - Only show for inbound */}
      {emailDirection === "inbound" && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Object.entries(statusCounts).map(([status, count]) => {
          const config = statusConfig[status];
          const Icon = config?.icon || Clock;
          return (
            <Card
              key={status}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedStatus === status ? "ring-2 ring-blue-500" : ""
              }`}
              onClick={() => setSelectedStatus(selectedStatus === status ? "" : status)}
              data-testid={`status-filter-${status}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{config?.label || status}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${config?.color?.split(" ")[1] || "text-slate-400"}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* Filters & Actions */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={emailDirection === "outbound" ? "Search by recipient, subject..." : "Search by subject, sender, ticket..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="email-search-input"
            />
          </div>
        </div>
        
        {emailDirection === "inbound" && (
        <>
        <Select value={selectedSection || "all"} onValueChange={(val) => setSelectedSection(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[200px]" data-testid="section-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sections</SelectItem>
            {Object.entries(sectionLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label} ({sectionCounts[key] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedInbox || "all"} onValueChange={(val) => setSelectedInbox(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[220px]" data-testid="inbox-filter">
            <Mail className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by inbox" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(inboxConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.label} {key !== "all" && `(${inboxCounts[key] || 0})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </>
        )}

        <Button variant="outline" onClick={() => fetchEmails()} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>

        {emailDirection === "inbound" && (
        <Button onClick={() => setShowUploadModal(true)} data-testid="upload-email-btn">
          <Upload className="h-4 w-4 mr-2" />
          Upload .eml/.msg
        </Button>
        )}
      </div>

      {/* Email Triggers Info - Only for inbound */}
      {emailDirection === "inbound" && (
      <Card className="mb-6">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Subject Triggers
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-2">
            {Object.entries(triggers).map(([trigger, section]) => (
              <Badge key={trigger} variant="outline" className="font-mono">
                {trigger} → {sectionLabels[section] || section}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Email List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No emails found</p>
            </div>
          ) : (
            <div className="divide-y">
              {emails.map((email) => {
                const StatusIcon = statusConfig[email.status]?.icon || Clock;
                const SectionIcon = sectionIcons[email.target_section] || Mail;
                const isOutbound = email.direction === "outbound" || emailDirection === "outbound";
                
                return (
                  <div
                    key={email.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedEmail(email);
                      setShowDetailModal(true);
                    }}
                    data-testid={`email-item-${email.ticket_number || email.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`p-2 rounded-lg ${isOutbound ? "bg-blue-100" : (statusConfig[email.status]?.color || "bg-slate-100")}`}>
                          {isOutbound ? <Send className="h-5 w-5 text-blue-600" /> : <SectionIcon className="h-5 w-5" />}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {!isOutbound && (
                            <>
                            <Badge variant="outline" className="font-mono text-xs">
                              {email.ticket_number || "No ticket"}
                            </Badge>
                            <Badge className={statusConfig[email.status]?.color || "bg-slate-100"}>
                              {statusConfig[email.status]?.label || email.status}
                            </Badge>
                            <Badge variant="secondary">
                              {sectionLabels[email.target_section] || email.target_section}
                            </Badge>
                            </>
                          )}
                          {isOutbound && (
                            <>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              <Send className="h-3 w-3 mr-1" />
                              Sent
                            </Badge>
                            <Badge variant="secondary">
                              {email.email_type || "outbound"}
                            </Badge>
                            </>
                          )}
                          {!isOutbound && email.inbox_account && inboxConfig[email.inbox_account] && (
                            <Badge variant="outline" className={inboxConfig[email.inbox_account].color}>
                              {inboxConfig[email.inbox_account].label}
                            </Badge>
                          )}
                          {email.parsed_data && (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <Sparkles className="h-3 w-3 mr-1" />
                              Parsed
                            </Badge>
                          )}
                          {email.assigned_staff_name && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              <User className="h-3 w-3 mr-1" />
                              {email.assigned_staff_name}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-slate-800 truncate">{email.subject}</h3>
                        <p className="text-sm text-slate-500">
                          {isOutbound 
                            ? `To: ${(email.to_email && !email.to_email.includes('image') && email.to_email.includes('@')) ? email.to_email : (email.customer_email || 'Unknown recipient')} • ${formatDate(email.sent_at || email.date)}`
                            : `From: ${email.from_name || email.from_email} • ${formatDate(email.received_at)}`
                          }
                        </p>
                        {isOutbound && email.body_preview && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-1">{email.body_preview}</p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="py-2 px-4 text-sm text-slate-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Email Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedEmail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {selectedEmail.direction === "outbound" || emailDirection === "outbound" ? (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      <Send className="h-3 w-3 mr-1" />
                      Sent
                    </Badge>
                  ) : (
                    <>
                    <Badge variant="outline" className="font-mono">
                      {selectedEmail.ticket_number}
                    </Badge>
                    <Badge className={statusConfig[selectedEmail.status]?.color}>
                      {statusConfig[selectedEmail.status]?.label}
                    </Badge>
                    </>
                  )}
                  {selectedEmail.email_type && (
                    <Badge variant="secondary">{selectedEmail.email_type}</Badge>
                  )}
                </div>
                <DialogTitle>{selectedEmail.subject}</DialogTitle>
                <DialogDescription>
                  {selectedEmail.direction === "outbound" || emailDirection === "outbound" ? (
                    <>
                      To: {selectedEmail.to_email || selectedEmail.customer_email}
                      <br />
                      From: {selectedEmail.sender_email || selectedEmail.from_email || "roket@europacific.com"}
                      <br />
                      Sent: {formatDate(selectedEmail.sent_at || selectedEmail.date)}
                      {selectedEmail.related_id && (
                        <>
                          <br />
                          Related: {selectedEmail.related_id}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      From: {selectedEmail.from_name} &lt;{selectedEmail.from_email}&gt;
                      <br />
                      Received: {formatDate(selectedEmail.received_at)}
                      <br />
                      Section: {sectionLabels[selectedEmail.target_section]}
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Email Body */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Email Content</h4>
                  <div className="text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {selectedEmail.body || selectedEmail.body_preview || "(No content available)"}
                  </div>
                </div>

                {/* Metadata for outbound emails */}
                {(selectedEmail.direction === "outbound" || emailDirection === "outbound") && selectedEmail.metadata && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium mb-2">Email Details</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedEmail.metadata.voucher_code && (
                        <div><span className="text-slate-500">Voucher:</span> <span className="font-mono">{selectedEmail.metadata.voucher_code}</span></div>
                      )}
                      {selectedEmail.metadata.company && (
                        <div><span className="text-slate-500">Company:</span> {selectedEmail.metadata.company}</div>
                      )}
                      {selectedEmail.metadata.stage && (
                        <div><span className="text-slate-500">Stage:</span> {selectedEmail.metadata.stage}</div>
                      )}
                      {selectedEmail.metadata.region && (
                        <div><span className="text-slate-500">Region:</span> {selectedEmail.metadata.region}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Parsed Data - only for inbound */}
                {selectedEmail.direction !== "outbound" && emailDirection !== "outbound" && selectedEmail.parsed_data && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-green-600" />
                      AI Parsed Data
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedEmail.parsed_data.customer_company && (
                        <div><span className="text-slate-500">Company:</span> {selectedEmail.parsed_data.customer_company}</div>
                      )}
                      {selectedEmail.parsed_data.service_type && (
                        <div><span className="text-slate-500">Service:</span> {selectedEmail.parsed_data.service_type}</div>
                      )}
                      {selectedEmail.parsed_data.origin_city && (
                        <div><span className="text-slate-500">Origin:</span> {selectedEmail.parsed_data.origin_city}, {selectedEmail.parsed_data.origin_country}</div>
                      )}
                      {selectedEmail.parsed_data.destination_city && (
                        <div><span className="text-slate-500">Destination:</span> {selectedEmail.parsed_data.destination_city}, {selectedEmail.parsed_data.destination_country}</div>
                      )}
                      {selectedEmail.parsed_data.container_type && (
                        <div><span className="text-slate-500">Container:</span> {selectedEmail.parsed_data.container_quantity}x {selectedEmail.parsed_data.container_type}</div>
                      )}
                      {selectedEmail.parsed_data.commodity && (
                        <div><span className="text-slate-500">Commodity:</span> {selectedEmail.parsed_data.commodity}</div>
                      )}
                      {selectedEmail.parsed_data.incoterm && (
                        <div><span className="text-slate-500">Incoterm:</span> {selectedEmail.parsed_data.incoterm}</div>
                      )}
                      {selectedEmail.parsed_data.cargo_ready_date && (
                        <div><span className="text-slate-500">Ready Date:</span> {selectedEmail.parsed_data.cargo_ready_date}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedEmail.notes?.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Notes</h4>
                    <div className="space-y-2">
                      {selectedEmail.notes.map((note, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-slate-500">{formatDate(note.at)}:</span> {note.text || note.type}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Staff Assignment Info */}
                {selectedEmail.assigned_staff_name && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium mb-1 flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      Assigned To
                    </h4>
                    <p className="text-sm text-blue-800 font-medium">{selectedEmail.assigned_staff_name}</p>
                    {selectedEmail.assigned_at && (
                      <p className="text-xs text-blue-600 mt-1">Assigned: {formatDate(selectedEmail.assigned_at)}</p>
                    )}
                  </div>
                )}

                {/* Add Note - only for inbound */}
                {selectedEmail.direction !== "outbound" && emailDirection !== "outbound" && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                  />
                  <Button variant="outline" onClick={handleAddNote} disabled={addingNote}>
                    {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                  </Button>
                </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {selectedEmail.direction !== "outbound" && emailDirection !== "outbound" && selectedEmail.status !== "converted" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleParseEmail(selectedEmail)}
                      disabled={parsing}
                    >
                      {parsing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {selectedEmail.parsed_data ? "Re-Parse" : "Parse with AI"}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setReassignSection(selectedEmail.target_section);
                        setShowReassignModal(true);
                      }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Reassign
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedStaffId(selectedEmail.assigned_staff_id || "");
                        setShowAssignStaffModal(true);
                      }}
                      data-testid="assign-staff-btn"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      {selectedEmail.assigned_staff_name ? "Reassign Staff" : "Assign Staff"}
                    </Button>
                    
                    {(selectedEmail.target_section === "quotation" || selectedEmail.target_section === "unassigned") && (
                      <Button onClick={() => handleConvertToQuote(selectedEmail)} disabled={converting}>
                        {converting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Convert to Quote
                      </Button>
                    )}
                    
                    <Button variant="ghost" onClick={() => handleArchive(selectedEmail)}>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </Button>
                  </>
                )}
                
                {/* Close button for outbound emails */}
                {(selectedEmail.direction === "outbound" || emailDirection === "outbound") && (
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                    Close
                  </Button>
                )}
                
                {selectedEmail.converted_to && (
                  <div className="w-full text-sm text-green-600 bg-green-50 p-2 rounded">
                    Converted to {selectedEmail.converted_to}: {selectedEmail.converted_quote_number || selectedEmail.converted_id}
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign Modal */}
      <Dialog open={showReassignModal} onOpenChange={setShowReassignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Email</DialogTitle>
            <DialogDescription>Select the section to route this email to</DialogDescription>
          </DialogHeader>
          <Select value={reassignSection} onValueChange={setReassignSection}>
            <SelectTrigger>
              <SelectValue placeholder="Select section" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(sectionLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignModal(false)}>Cancel</Button>
            <Button onClick={handleReassign}>Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Email</DialogTitle>
            <DialogDescription>
              Upload an .eml or .msg file to add it to the repository. Use subject triggers like (QUOTE), (RFQ), (PF) for automatic routing.
            </DialogDescription>
          </DialogHeader>
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto mb-4 text-slate-400" />
            <input
              type="file"
              accept=".eml,.msg"
              onChange={handleUploadEml}
              className="hidden"
              id="eml-upload"
            />
            <label
              htmlFor="eml-upload"
              className="cursor-pointer text-blue-600 hover:underline"
            >
              Click to select .eml or .msg file
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Staff Modal */}
      <Dialog open={showAssignStaffModal} onOpenChange={setShowAssignStaffModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to Staff Member</DialogTitle>
            <DialogDescription>
              Select a staff member to handle this email request
            </DialogDescription>
          </DialogHeader>
          <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
            <SelectTrigger data-testid="staff-select">
              <SelectValue placeholder="Select staff member..." />
            </SelectTrigger>
            <SelectContent>
              {staffList.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{staff.name}</span>
                    {staff.department && (
                      <span className="text-xs text-slate-400">({staff.department})</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {staffList.length === 0 && (
            <p className="text-sm text-amber-600">No staff members found. Please add staff users to the system.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignStaffModal(false)}>Cancel</Button>
            <Button onClick={handleAssignStaff} disabled={assigningStaff || !selectedStaffId}>
              {assigningStaff ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <User className="h-4 w-4 mr-2" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
